<template>
  <cst-page-template>
    <div class="container">
      <back-to-dashboard-link :type="dashboardType.purchaseOrder" />

      <div v-if="isLoaded" class="purchase-order-page--wrapper">
        <div class="purchase-order-page__header">
          <h1>{{ i18n.t('/purchaseorder/title') }}</h1>
          <h6
            v-if="purchaseOrder.Status"
            class="purchase-order-details-block__status"
            data-qa="purchase-order-configuration-page-status"
          >
            {{ i18n.t('/purchaseorder/status') }} {{ purchaseOrder.Status }}
          </h6>
          <h6
            v-if="purchaseOrder.PurchaseOrderId > 0"
            class="purchase-order-details-block__status"
            data-qa="purchase-order-configuration-page-order-number"
          >
            {{ i18n.t('/purchaseorder/purchaseorderid') }} {{ purchaseOrder.PurchaseOrderId }}
          </h6>
        </div>

        <div v-if="!configIsValid">
          <p v-if="!statusDetails?.Title" class="m-message-bar m-message-bar--warning">
            {{ i18n.t('/purchaseorder/invalidconfiguration') }}
          </p>
          <div v-if="statusDetails" class="m-message-bar m-message-bar--warning u-dont-break-out">
            <p>
              {{ statusDetails.Title }}<br />
              <template v-for="(item, index) in statusDetails.Body" :key="index">
                {{ item }}<br />
              </template>
              <a :href="statusDetails.LinkHref" target="_blank">{{ statusDetails.LinkText }}</a>
            </p>
          </div>
        </div>

        <h3 class="purchase-order-details-block__header">
          {{ i18n.t('/purchaseorder/orderdetailsheader') }}
        </h3>
        <new-purchase-order-form
          :purchase-order-id="purchaseOrderId"
          :purchase-order-details="purchaseOrder.Details"
          :car-statuses="purchaseOrder.CarStatuses"
        />

        <images-block />

        <div class="purchase-order-details-block__header">
          <h3 class="purchase-order-details-block__section-heading">
            {{ i18n.t('/purchaseorder/configurationheader') }}
          </h3>
          <div
            class="purchase-order-details-block__config-code"
            data-qa="purchase-order-configuration-page-config-code-label"
          >
            <p>
              {{ i18n.t('/purchaseorder/configurationcode') }}
              {{ purchaseOrder.Details.Car.ConfigurationCode }}
            </p>
          </div>
        </div>

        <configuration-summary />

        <h3 class="purchase-order-details-block__header">
          {{ i18n.t('/purchaseorder/extraheader') }}
        </h3>
        <purchase-order-extras-block />

        <horizontal-line class="horizontalLine" />

        <h3 class="section-header">{{ i18n.t('/purchaseorder/propositionheader') }}</h3>
        <purchase-order-proposition-block />
        <horizontal-line class="horizontalLine" />

        <price-block v-if="purchaseOrder.MetaData.Prices" :prices="purchaseOrder.MetaData.Prices" />
        <customer-type-prompt
          :show="showCustomerTypePrompt"
          :is-purchase-order-page="true"
          @confirm="showCustomerTypePrompt = false"
          @close="showCustomerTypePrompt = false"
        />
        <actions :is-customer-valid="isCustomerValid" @has-customer-type="hasCustomerType" />
        <expected-delivery-date-prompt :show="showNoLiBPrompt" @close="showNoLiBPrompt = false" />
      </div>
    </div>
  </cst-page-template>
</template>

<script>
import CustomerTypePrompt from '@/components/generic/presentational/molecules/customerTypePrompt/CustomerTypePrompt.vue'
import CstPageTemplate from '@/templates/CstPageTemplate.vue'
import NewPurchaseOrderForm from '@/components/purchaseOrder/presentational/organisms/newCustomerSearch/PurchaseOrderForm.vue'
import PurchaseOrderExtrasBlock from '@/components/generic/presentational/organisms/extrasBlock/PurchaseOrderExtrasBlock.vue'
import PurchaseOrderPropositionBlock from '@/components/generic/presentational/molecules/purchaseOrderPropositionBlock/PurchaseOrderPropositionBlock.vue'
import purchaseOrderService from '@/services/purchaseOrderService.js'
import ConfigurationSummary from '@/components/generic/presentational/organisms/configurationSummary/ConfigurationSummary.vue'
import ImagesBlock from '@/components/generic/presentational/molecules/imagesBlock/ImagesBlock.vue'
import PriceBlock from '@/components/purchaseOrder/presentational/molecules/priceBlock/PriceBlock.vue'
import Actions from '@/components/purchaseOrder/presentational/molecules/actions/Actions.vue'
import HorizontalLine from '@/components/generic/presentational/atoms/horizontalLine/HorizontalLine.vue'
import BackToDashboardLink from '@/components/generic/presentational/molecules/backToDashboardLink/BackToDashboardLink.vue'
import dashboardType from '@/helpers/enums/dashboardType'
import { mapGetters, mapState } from 'vuex'
import ExpectedDeliveryDatePrompt from '@/components/purchaseOrder/presentational/molecules/expectedDeliveryDatePrompt/ExpectedDeliveryDatePrompt.vue'
import CheckExpectedDeliveryDatePurchaseOrder from '@/mixins/checkExpectedDeliveryDatePurchaseOrder'
import { EventBus } from '@/plugins/event-bus'

export default {
  components: {
    CustomerTypePrompt,
    CstPageTemplate,
    NewPurchaseOrderForm,
    PurchaseOrderExtrasBlock,
    ImagesBlock,
    ConfigurationSummary,
    PriceBlock,
    HorizontalLine,
    Actions,
    BackToDashboardLink,
    PurchaseOrderPropositionBlock,
    ExpectedDeliveryDatePrompt,
  },
  mixins: [CheckExpectedDeliveryDatePurchaseOrder],
  props: {
    purchaseOrderId: {
      type: String,
      default: '',
    },
    configurationCode: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isLoaded: false,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      dashboardType: dashboardType,
      showCustomerTypePrompt: false,
    }
  },
  computed: {
    ...mapGetters({
      purchaseOrder: 'purchaseOrder/get',
      isCustomerValid: 'purchaseOrder/isCustomerValid',
    }),

    ...mapState('configuration', ['configuration']),
    ...mapState('user', ['dealerSettings']),
    configIsValid() {
      return this.configuration?.Status?.IsValid
    },
    statusDetails() {
      return this.configuration?.Status?.InvalidInfo?.InvalidMessageUser
    },
  },
  async created() {
    EventBus.$on('delivery-dates-loaded', this.onDeliveryDatesLoaded)
    if (this.purchaseOrderId) {
      // load existing purchase order
      await purchaseOrderService.getPurchaseOrder(this.purchaseOrderId)
    } else if (this.configurationCode) {
      // create new purchase order
      await purchaseOrderService.initNewPurchaseOrder(this.configurationCode)
    }
    this.isLoaded = true
  },
  unmounted() {
    EventBus.$off('delivery-dates-loaded', this.onDeliveryDatesLoaded)
  },
  methods: {
    hasCustomerType(data) {
      this.showCustomerTypePrompt = !data
    },
    onDeliveryDatesLoaded() {
      if (this.purchaseOrder?.Status !== 'PURCHASEORDER') {
        this.expectedDeliveryDateCheck()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  @include center;
  display: grid;
  row-gap: 20px;
}

.purchase-order {
  &-page__header {
    text-align: center;
  }

  &-page__top {
    display: flex;
    gap: 1.2em;

    > div.main {
      width: 66%;
    }
    > div.sidebar {
      width: 33%;
    }
  }
}

h3 {
  font-size: 1.72122rem;
  color: $color-primary;
  margin-bottom: 0;
}
</style>
